import React from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { Text } from '@/components/common/text';
import { AnchoredDropdownItem } from '@/components/common/admin/desktop/dropdown/AnchoredDropdownItem';
import { showConfirm } from '@/utils/common/alert';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IConversation } from '@/types/admin/messages';
import { useArchiveConversationMutation } from '@/store/api/admin/messagesApi';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

// folder.svg, archive.svg and admin.svg bake their stroke in as gray400 and
// ignore `color`; pencil.svg honours it, so it is told the same gray to match.
const SIBLING_STROKE_GRAY = PRIMITIVE_COLORS.gray400;

interface ChatOptionsMenuProps {
  visible: boolean;
  onClose: () => void;
  conversation: IConversation;
  onEditPress: () => void;
  onThreadOwnerPress: () => void;
  onShowDetailsPress: () => void;
  position?: {
    top: number;
    right: number;
  };
}

export const ChatOptionsMenu: React.FC<ChatOptionsMenuProps> = ({
  visible,
  onClose,
  conversation,
  onEditPress,
  onThreadOwnerPress,
  onShowDetailsPress,
  position = { top: 60, right: 16 },
}) => {
  const { redesign, admin } = useAdminColors();
  const [archiveConversation, { isLoading: isArchiving }] = useArchiveConversationMutation();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();

  const handleArchive = async () => {
    const confirmed = await showConfirm(
      'Archive Conversation',
      'Are you sure you want to archive this conversation? It will be moved to the archived section.',
      'Archive',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await archiveConversation(conversation.id).unwrap();
      showSuccess('Conversation archived successfully');
      onClose();
    } catch (error: any) {
      console.error('Archive conversation error:', error);
      showApiError(error?.data?.message || 'Failed to archive conversation');
    }
  };

  const menuOptions = [
    {
      id: 'details',
      label: 'Show Details',
      icon: 'INFO',
      onPress: () => {
        onClose();
        onShowDetailsPress();
      },
    },
    {
      id: 'edit',
      label: 'Edit Conversation',
      icon: 'EDIT',
      onPress: () => {
        onClose();
        onEditPress();
      },
    },
    {
      id: 'archive',
      label: 'Archive Conversation',
      icon: 'ARCHIVE',
      onPress: handleArchive,
      disabled: isArchiving,
    },
    {
      id: 'owner',
      label: 'Thread Owner',
      icon: 'USER_MANAGEMENT',
      onPress: () => {
        onClose();
        onThreadOwnerPress();
      },
    },
  ];

  if (!visible) return null;

  const screenWidth = Dimensions.get('window').width;
  const menuWidth = 240;

  // Ensure menu doesn't go off screen
  const adjustedRight = Math.max(16, Math.min(position.right, screenWidth - menuWidth - 16));

  return (
    <View style={[
      styles.dropdownContainer,
      {
        top: position.top,
        right: adjustedRight,
      }
    ]}>
      <GlassCard padding={0} style={styles.menuContainer}>
        <View style={styles.menuContent}>
          {menuOptions.map((option) => {
            const iconColor = option.disabled ? redesign.text.subtle : redesign.text.primary;
            const textColor = option.disabled ? redesign.text.subtle : redesign.text.primary;
            return (
              <AnchoredDropdownItem
                key={option.id}
                style={[
                  styles.menuOption,
                  option.disabled && styles.menuOptionDisabled,
                ]}
                onPress={option.onPress}
                disabled={option.disabled}
              >
                <View style={styles.menuOptionIcon}>
                  {option.icon === 'INFO' && (
                    <SVG_COMPONENTS.FOLDER
                      width={18}
                      height={18}
                      color={iconColor}
                    />
                  )}
                  {option.icon === 'EDIT' && (
                    <SVG_COMPONENTS.PENCIL
                      width={18}
                      height={18}
                      color={SIBLING_STROKE_GRAY}
                    />
                  )}
                  {option.icon === 'ARCHIVE' && (
                    <SVG_COMPONENTS.ARCHIVE
                      width={18}
                      height={18}
                      color={iconColor}
                    />
                  )}
                  {option.icon === 'USER_MANAGEMENT' && (
                    <SVG_COMPONENTS.ADMIN
                      width={18}
                      height={18}
                      color={iconColor}
                    />
                  )}
                </View>

                <Text style={[styles.menuOptionText, { color: textColor }]}>
                  {option.label}
                </Text>
              </AnchoredDropdownItem>
            );
          })}
        </View>
      </GlassCard>
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: 'absolute',
    zIndex: 1000,
    width: 240,
  },
  menuContainer: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  menuContent: {
    paddingVertical: 4,
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'transparent',
  },
  menuOptionDisabled: {
    opacity: 0.5,
  },
  menuOptionIcon: {
    width: 24,
    alignItems: 'center',
    marginRight: 12,
  },
  menuOptionText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
  },
});
